import { useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useSplunkLogger } from '@resolutionlife/ui';

import * as ToastConfigs from '../../../configs/toast';
import {
  EventFragment,
  EventType,
  useGetFileActivityEventsQuery,
} from '../../../graphql/generated';

type UseFileActivityEventsBag = {
  error: boolean;
  loading: boolean;
  retry: () => void;
  hasMore?: boolean;
  cedantName?: string;
  isFetchingMore: boolean;
  events?: EventFragment[];
  fetchMore: (cursor: string) => () => void;
};

export const useFileActivityEvents = (
  cedant: string,
  eventType?: EventType
): UseFileActivityEventsBag => {
  const toast = useToast();
  const splunkLogger = useSplunkLogger();
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const {
    error,
    loading,
    data,
    refetch,
    fetchMore,
  } = useGetFileActivityEventsQuery({
    variables: {
      cedant,
      eventTypes: eventType ? [eventType] : undefined,
    },
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
  });

  const updateQuery = (prev: any, { fetchMoreResult }: any) => {
    if (!fetchMoreResult?.cedant?.events?.events || !prev.cedant) return prev;
    return {
      ...prev,
      cedant: {
        ...prev.cedant,
        __typename: "CedantDetails",
        events: {
          ...prev.cedant.events,
          __typename: "EventsResponse",
          events: [...(prev.cedant.events?.events || []), ...fetchMoreResult.cedant.events.events],
          hasMore: fetchMoreResult.cedant.events.hasMore,
        },
      },
    };
  };

  const handleFetchMore = (cursor: string) => async () => {
    try {
      setIsFetchingMore(true);
      await fetchMore({
        variables: {
          cedant,
          eventTypes: eventType ? [eventType] : undefined,
          pagination: { cursor },
        },
        updateQuery,
      });
    } catch (error) {
      console.error('FetchMore error:', error);
      toast(ToastConfigs.problemCompletingRequest);
      splunkLogger.error('Activity feed pagination failed', { eventCategory: 'activity_feed_error', cedant, errorMessage: String(error) });
    } finally {
      setIsFetchingMore(false);
    }
  };

  return {
    loading,
    error: !!error,
    retry: () => { refetch(); },
    isFetchingMore,
    fetchMore: handleFetchMore,
    cedantName: data?.cedant?.name,
    events: data?.cedant?.events.events,
    hasMore: data?.cedant?.events.hasMore,
  };
};
